import {useEffect, useRef} from "react";
import { Box, Title, Text, Select, Textarea, Group, Button } from "@mantine/core";
import {  IconCheck } from '@tabler/icons';
import { useForm } from "@mantine/form";
import { useNavigate } from 'react-router-dom';
import { closeAllModals } from '@mantine/modals';
import { saveIdea, deleteGeneratedIdea } from '../../../Api';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';

export function DeclineGeneratedIdeaModal({ idea, type }: { idea: any, type: 'decline' | 'archive' }) {
  const form = useForm({
    initialValues: {
      reason: "",
      comment: "",
    },
    validate: {
      reason: (value: string) => (value ? null : "Reason is required"),
      comment: (value: string, values: { reason: string }) => {
        if (values.reason === "Other") {
          return value && value.length >= 5
            ? null
            : "Comment must be at least 5 characters";
        }
        return null; // not required otherwise
      },
    },
  });

  const auth0 = useAuth0();
  const navigate = useNavigate();

  const handleDecline = (values: any) => {
    const deleteInfo = {
      reason: values.reason,  
      comment: values.comment,
    };
    deleteGeneratedIdea(idea?._id, deleteInfo, auth0)
      .then((response) => {
        closeAllModals();
        navigate("/ideas");
      })
      .catch((e: any) => console.log(e));
  }

  const handleArchive = (values: any) => {
    const isArchived = idea.state === "archived";
    const archiveInfo = {
      reason: values.reason,  
      comment: values.comment,
    };
    const request = {
      id: idea?._id,
      state: isArchived ? "active" : "archived",
      archiveInfo
    };
    saveIdea(request, auth0)
      .then((res) => {
        showNotification({
          title: isArchived ? "Idea unarchived" : "Idea archived",
          color: "teal",
          message: <Text
            onClick={() => {
              request.state = isArchived ? "archived" : "active";
              saveIdea(request, auth0)
                .then(() => {            
                  showNotification({
                    id: 'idea-archive',
                    title: isArchived ? "Idea archived" : "Idea unarchived",
                    message: `Idea has been restored to ${idea.state}`,
                    color: "teal",
                    autoClose: 3000,
                    icon: <IconCheck size="1.1rem" />,
                  });
                })
                .catch((e) => console.error("Undo failed", e));
            }}
            style={{
              color: "#228be6",
              cursor: "pointer",
              fontWeight: 500,
              display: "inline-block",
              marginTop: 8,
            }}
          >
                  Undo
          </Text>,
          icon: <IconCheck size="1.1rem" />,
          autoClose: 1200
        });
        closeAllModals();
      })
      .catch((e) => console.log(e));
  }

  const prevReason = useRef<string | null>(null);

  useEffect(() => {
    if (prevReason.current === "Other" && form.values.reason !== "Other") {
      // user changed from "other" → something else
      form.validateField("comment");
    }
    prevReason.current = form.values.reason;
  }, [form.values.reason]);

  return (
    <Box>
      <Box>
        <Title color="#5B60E3" order={5} align="center">
          Why do you want to {type === 'archive' ? 'archive' : 'delete'} this AI generated idea?
        </Title>
      </Box>

      <Box m="xl" sx={{ width: 400 }} mx="auto">
        <form onSubmit={form.onSubmit(type === 'archive' ? handleArchive : handleDecline)}>
          <Select
            mb="md"
            label="Reason"
            placeholder="Select reason"
            withAsterisk
            data={[
              "Duplicate: Similar Idea already exists in Bagel",
              "Feature/Capability already exists",
              "Idea too broad",
              "Other",
            ]}
            {...form.getInputProps("reason")}
          />

          <Textarea
            withAsterisk={form.values.reason === "Other"}
            placeholder="Add your comment"
            label="Add comment"
            {...form.getInputProps("comment")}
          />

          <Group position="center" mt="xl">
            <Button
              mt="xl"
              sx={{
                backgroundColor: "#5B60E3",
                ":hover": { backgroundColor: "#4c50ba" },
                fontWeight: 500,
                fontSize: 14,
              }}
              type="submit"
              size="md"
            >
              {type === 'archive' ? 'Archive' : 'Decline'}
            </Button>
          </Group>
        </form>
      </Box>
    </Box>
  );
}

